import { useState, useEffect } from 'react';
import CustomerDiagnose from './CustomerDiagnose.jsx';
import { useMyFlags, flagOn } from '../lib/flags.js';

// Reusable email audit: scheduled-next + a filterable sent log. `load(params)`
// returns { log, upcoming }; admin passes a client column, client surfaces hide
// it (it's always their own).
const KINDS = { digest: '🗓 Digest', campaign: '⚡ Campaign', notification: '📥 Notification', test: '🧪 Test', other: 'Other' };
const fmtT = (iso) => { try { return new Date(iso).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } };
const statusColor = (s) => (s === 'sent' ? 'var(--success, #10b981)' : s === 'failed' ? 'var(--error, #ef4444)' : 'var(--muted)');

// `entityId` switches on diagnosis for the WHOLE view — a client surface always
// has one. The admin-wide view spans clients, so it passes none; there each ROW
// still carries its own entity, which is enough to diagnose that row's address
// against that row's client. Either way a diagnose call names exactly one
// entity, and the server checks access to it.
export default function MailLogView({ load, showClient = false, entityId = '', reloadKey = '' }) {
  // Diagnosis rides the `engage.diagnose` switch. The route gate is the real
  // boundary; this stops a client being offered a door that answers 403. On the
  // ADMIN-WIDE view there is no entityId (it spans clients, and each row carries
  // its own), so useMyFlags reads everything on — correct, because the gate
  // resolves a Howler admin against staff defaults rather than one client.
  const myFlags = useMyFlags(entityId);
  const canDiagnose = !entityId || flagOn(myFlags, 'engage.diagnose');
  const [data, setData] = useState(null);
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  // Filtering the loaded rows, NOT the query: the log holds only the most recent
  // sends, so an address that is genuinely absent here may still have a full
  // campaign history. Diagnosing goes to the ledgers instead — which is why the
  // empty state offers it rather than saying "not found".
  const [q, setQ] = useState('');
  // { email, entityId } — the pair a diagnosis needs. Null when nothing is open.
  const [who, setWho] = useState(null);
  const refresh = () => load({ kind, status, limit: 200 }).then(setData).catch(() => setData({ log: [], upcoming: [] }));
  useEffect(() => { refresh(); }, [kind, status, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setWho(null); }, [reloadKey]); // switching client must not leave another client's diagnosis open
  const diagnose = (email, rowEntityId) => {
    const id = entityId || rowEntityId || '';
    if (id && email) setWho({ email: String(email).split(/[,;]\s*/)[0].toLowerCase(), entityId: id });
  };

  if (!data) return <p style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</p>;
  const needle = q.trim().toLowerCase();
  const rows = needle
    ? data.log.filter((r) => `${r.recipient || ''} ${r.subject || ''}`.toLowerCase().includes(needle))
    : data.log;

  return (
    <div>
      {data.upcoming.length > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>⏭ Scheduled next</div>
          {data.upcoming.map((u) => (
            <div key={u.id} style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '5px 0', borderTop: '1px solid var(--hairline)', fontSize: 12.5, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600 }}>{u.title}</span>
              {showClient && <span style={{ color: 'var(--muted)' }}>{u.entityName}</span>}
              <span style={{ color: 'var(--muted)' }}>{u.recipients} recipient{u.recipients === 1 ? '' : 's'} · {u.cadence} {u.timeOfDay}</span>
              <span style={{ marginLeft: 'auto', color: 'var(--brand)', fontWeight: 600 }}>{fmtT(u.nextRunAt)}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <select style={sel} value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">All types</option>
          <option value="digest">Digests</option>
          <option value="campaign">Campaigns</option>
          <option value="notification">Notifications</option>
          <option value="test">Tests</option>
        </select>
        <select style={sel} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </select>
        {entityId && (
          <input
            style={{ ...sel, minWidth: 240, flex: '1 1 240px' }}
            placeholder="Search or diagnose an email address…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && canDiagnose && q.trim()) diagnose(q.trim()); }}
          />
        )}
        {entityId && canDiagnose && q.trim() && <button style={mini} onClick={() => diagnose(q.trim())}>Diagnose</button>}
        <button style={mini} onClick={refresh}>↻ Refresh</button>
      </div>

      {who && <CustomerDiagnose entityId={who.entityId} email={who.email} onClose={() => setWho(null)} />}

      <div style={card}>
        {rows.length === 0 ? (
          <p style={{ color: 'var(--muted)', fontSize: 13 }}>
            {q.trim()
              ? <>Nothing matching “{q.trim()}” in the recent log — it keeps only the last few thousand sends. {entityId
                ? (canDiagnose ? <button style={{ ...mini, marginLeft: 6 }} onClick={() => diagnose(q.trim())}>Diagnose this address</button> : null)
                : <span> Pick a client above to diagnose it anyway — the ledgers go back further than this log.</span>}</>
              : 'No emails yet.'}
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <th style={th}>When</th><th style={th}>Type</th><th style={th}>To</th><th style={th}>Subject</th>{showClient && <th style={th}>Client</th>}<th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--hairline)' }}>
                  <td style={{ ...th, whiteSpace: 'nowrap', color: 'var(--muted)' }}>{fmtT(r.at)}</td>
                  <td style={{ ...th, whiteSpace: 'nowrap' }}>{KINDS[r.kind] || r.kind}</td>
                  <td style={{ ...th, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {(entityId || r.entity_id)
                      ? (canDiagnose ? <button type="button" onClick={() => diagnose(r.recipient, r.entity_id)} title="See everything this person was sent, and why" style={linkBtn}>{r.recipient}</button> : <>{r.recipient}</>)
                      : r.recipient}
                    {r.event_id && (
                      <div style={{ fontSize: 10.5, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis' }} title="Howler Core event id sent to the email provider">
                        event {r.event_id}
                      </div>
                    )}
                  </td>
                  <td style={{ ...th, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.subject}>{r.subject}</td>
                  {showClient && <td style={{ ...th, color: 'var(--muted)' }}>{r.entityName}</td>}
                  <td style={{ ...th, whiteSpace: 'nowrap' }}>
                    <span style={{ fontWeight: 700, color: statusColor(r.status) }}>{r.status}</span>
                    {r.status !== 'sent' && r.detail && <span style={{ color: 'var(--muted)' }} title={r.detail}> · {String(r.detail).slice(0, 40)}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const card = { background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 12, padding: 14 };
const th = { padding: '7px 8px', verticalAlign: 'top' };
const sel = { padding: '8px 12px', border: '1.5px solid var(--hairline)', borderRadius: 8, fontSize: 13, background: 'var(--card)', color: 'var(--text)', outline: 'none' };
const linkBtn = { background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--brand)', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const mini = { padding: '7px 14px', background: 'rgba(128,128,128,0.10)', color: 'var(--text)', border: '1px solid var(--hairline)', borderRadius: 980, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
